import { cloneElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BadgeDollarSign,
  Calculator,
  Check,
  Clipboard,
  Clock3,
  Cloud,
  Coins,
  Factory,
  FileCheck2,
  FileImage,
  FileUp,
  Gauge,
  Leaf,
  LoaderCircle,
  Package,
  Percent,
  RefreshCcw,
  Save,
  ScanText,
  ShieldCheck,
  Sparkles,
  TimerReset,
  UserRound,
  WalletCards,
  X,
  Zap,
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { calculateEstimate, costPerGram, numberValue, scaleBatchProduction } from '../lib/pricing';
import { createFreshEstimateDraft } from '../lib/estimateDraft';
import { analyzeModelFile, isModelPrintFile } from '../lib/modelFile';
import { clearAppBusy, markAppBusy } from '../lib/workState';
import FieldHelp from './FieldHelp';
import UploadStarter from './UploadStarter';
import { isImageFile, recognizeSlicerScreenshot } from '../lib/ocr';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const preciseCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const defaultDraft = createFreshEstimateDraft();

const profileSnapshotFields = [
  'name',
  'materialType',
  'brand',
  'spoolPrice',
  'spoolWeightGrams',
  'electricityRate',
  'machineRate',
  'laborRate',
  'wastePercent',
  'fixedCost',
  'targetMargin',
];

const estimateInputFields = [
  'filamentGrams',
  'printHours',
  'printMinutes',
  'averagePowerWatts',
  'laborMinutes',
  'quantity',
  'additionalCost',
  'targetMargin',
];

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return Number(value) || 0;
}

function sortProfiles(items) {
  return [...items].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return timestampValue(b.updatedAt) - timestampValue(a.updatedAt);
  });
}

function draftStorageKey(userId) {
  return `makermargin:calculator-draft:${userId}`;
}

function restoreDraft(userId) {
  try {
    const stored = window.localStorage.getItem(draftStorageKey(userId));
    if (!stored) return defaultDraft;
    return { ...defaultDraft, ...JSON.parse(stored) };
  } catch {
    return defaultDraft;
  }
}

function readableError(error) {
  const code = error?.code || '';
  if (code.includes('permission-denied')) return 'Firestore blocked this pricing action. Confirm the published rules still allow your user subcollections.';
  if (code.includes('unavailable')) return 'MakerMargin could not reach Firebase. Check your connection and try again.';
  return error?.message || 'The pricing workspace could not complete that action.';
}

function copyProfileSnapshot(profile) {
  if (!profile) return null;
  return profileSnapshotFields.reduce((snapshot, field) => {
    snapshot[field] = profile[field] ?? '';
    return snapshot;
  }, {});
}

function copyEstimateInputs(draft) {
  return estimateInputFields.reduce((inputs, field) => {
    inputs[field] = numberValue(draft[field]);
    return inputs;
  }, {});
}

function copyResults(results) {
  return Object.entries(results).reduce((output, [key, value]) => {
    output[key] = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    return output;
  }, {});
}

function fileBaseName(name) {
  return String(name || 'Untitled estimate').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim() || 'Untitled estimate';
}

function fileKind(file) {
  if (isImageFile(file)) return 'image';
  const extension = String(file?.name || '').split('.').pop()?.toLowerCase();
  if (extension === 'gcode' || extension === 'gco') return 'gcode';
  if (extension === '3mf') return '3mf';
  if (extension === 'stl') return 'stl';
  return 'file';
}

function sourceMetadata(file) {
  return {
    name: String(file?.name || 'Uploaded print file'),
    type: String(file?.type || ''),
    size: Number(file?.size || 0),
    lastModified: Number(file?.lastModified || 0),
    kind: fileKind(file),
    importedAt: new Date().toISOString(),
    ocr: null,
  };
}

function confidenceLabel(value) {
  if (value >= 80) return 'High confidence';
  if (value >= 60) return 'Good confidence';
  if (value > 0) return 'Needs review';
  return 'Manual review';
}

function readableFileSize(bytes) {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function importedFieldLabel(field) {
  const labels = {
    filamentGrams: 'Filament weight',
    printHours: 'Print hours',
    printMinutes: 'Print minutes',
    quantity: 'Quantity',
  };
  return labels[field] || field;
}

function Field({ label, help, guidanceKey, prefix, suffix, children, className = '' }) {
  const controlId = useId();
  return (
    <div className={`calculator-field ${className}`}>
      <span className="calculator-field__label">
        <span><label htmlFor={controlId}>{label}</label>{help && <small>{help}</small>}</span>
        <FieldHelp guidanceKey={guidanceKey} />
      </span>
      <span className="calculator-field__control">
        {prefix && <b>{prefix}</b>}
        {cloneElement(children, { id: controlId })}
        {suffix && <b>{suffix}</b>}
      </span>
    </div>
  );
}

function BreakdownRow({ icon: Icon, label, detail, value, total }) {
  const share = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <div className="breakdown-row">
      <span className="breakdown-row__icon"><Icon size={16} /></span>
      <div className="breakdown-row__copy">
        <span><strong>{label}</strong><small>{detail}</small></span>
        <div className="breakdown-row__track"><i style={{ width: `${share}%` }} /></div>
      </div>
      <strong>{currency.format(value)}</strong>
    </div>
  );
}

export default function PricingCalculator({ user, onRequestAuth, onNavigate, loadEstimateId, onNewEstimate, initialUpload, onUploadConsumed, initialProduct, onProductConsumed }) {
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(Boolean(user));
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(() => (user ? restoreDraft(user.uid) : defaultDraft));
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [importState, setImportState] = useState({ status: 'idle', progress: 0, label: '' });
  const copyTimer = useRef(null);
  const saveTimer = useRef(null);
  const importToken = useRef('');
  const quantityScaleBasis = useRef(null);

  useEffect(() => () => {
    window.clearTimeout(copyTimer.current);
    window.clearTimeout(saveTimer.current);
  }, []);

  useEffect(() => {
    if (!user) {
      setProfiles([]);
      setLoadingProfiles(false);
      setDraft(defaultDraft);
      return undefined;
    }

    setDraft(restoreDraft(user.uid));
    setLoadingProfiles(true);
    const reference = collection(db, 'users', user.uid, 'costProfiles');
    return onSnapshot(reference, (snapshot) => {
      const items = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      setProfiles(sortProfiles(items));
      setLoadingProfiles(false);
    }, (snapshotError) => {
      setError(readableError(snapshotError));
      setLoadingProfiles(false);
    });
  }, [user]);

  useEffect(() => {
    if (!user || !loadEstimateId || !db) return;
    let active = true;
    setLoadingEstimate(true);
    setError('');

    getDoc(doc(db, 'users', user.uid, 'estimates', loadEstimateId))
      .then((snapshot) => {
        if (!active) return;
        if (!snapshot.exists()) throw new Error('That saved estimate no longer exists.');
        const estimate = snapshot.data();
        setDraft({
          ...defaultDraft,
          ...(estimate.inputs || {}),
          estimateId: snapshot.id,
          name: estimate.name || 'Untitled estimate',
          profileId: estimate.profileId || '',
          profileSnapshot: estimate.profileSnapshot || null,
          source: estimate.source || null,
        });
        setImportState(estimate.source ? { status: 'complete', progress: 1, label: 'Saved source details restored.' } : { status: 'idle', progress: 0, label: '' });
        setSaveStatus('Saved estimate opened.');
      })
      .catch((loadError) => active && setError(readableError(loadError)))
      .finally(() => active && setLoadingEstimate(false));

    return () => { active = false; };
  }, [loadEstimateId, user]);

  useEffect(() => {
    if (!user || !profiles.length) return;

    const currentExists = profiles.some((profile) => profile.id === draft.profileId);
    if (currentExists || (draft.profileSnapshot && draft.profileId)) return;

    const nextProfile = profiles.find((profile) => profile.isDefault) || profiles[0];
    setDraft((current) => ({
      ...current,
      profileId: nextProfile.id,
      profileSnapshot: null,
      targetMargin: numberValue(nextProfile.targetMargin),
    }));
  }, [draft.profileId, draft.profileSnapshot, profiles, user]);

  useEffect(() => {
    if (!user) return;
    try {
      window.localStorage.setItem(draftStorageKey(user.uid), JSON.stringify(draft));
    } catch {
      // A blocked local-storage write should never stop live calculations.
    }
  }, [draft, user]);

  useEffect(() => {
    if (!saveStatus) return undefined;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => setSaveStatus(''), 2600);
    return () => window.clearTimeout(saveTimer.current);
  }, [saveStatus]);

  const selectedProfile = profiles.find((profile) => profile.id === draft.profileId) || draft.profileSnapshot || null;
  const results = useMemo(
    () => calculateEstimate(selectedProfile, draft),
    [draft, selectedProfile],
  );

  const update = (field) => (event) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
  };

  const beginQuantityEdit = () => {
    quantityScaleBasis.current = {
      quantity: draft.quantity,
      filamentGrams: draft.filamentGrams,
      printHours: draft.printHours,
      printMinutes: draft.printMinutes,
    };
  };

  const updateQuantity = (event) => {
    const rawQuantity = event.target.value;
    if (rawQuantity === '') {
      setDraft((current) => ({ ...current, quantity: '' }));
      return;
    }
    setDraft((current) => {
      const basis = quantityScaleBasis.current || {
        quantity: current.quantity,
        filamentGrams: current.filamentGrams,
        printHours: current.printHours,
        printMinutes: current.printMinutes,
      };
      if (!quantityScaleBasis.current) quantityScaleBasis.current = basis;
      return { ...current, ...scaleBatchProduction(basis, rawQuantity, basis.quantity) };
    });
  };

  const finishQuantityEdit = () => {
    const basisSnapshot = quantityScaleBasis.current;
    setDraft((current) => {
      const basis = basisSnapshot || current;
      const finalQuantity = Math.max(1, Math.floor(numberValue(current.quantity, numberValue(basis.quantity, 1))));
      return { ...current, ...scaleBatchProduction(basis, finalQuantity, basis.quantity) };
    });
    quantityScaleBasis.current = null;
  };

  const selectProfile = (event) => {
    const profile = profiles.find((item) => item.id === event.target.value);
    if (!profile) return;
    setDraft((current) => ({
      ...current,
      profileId: profile.id,
      profileSnapshot: null,
      targetMargin: numberValue(profile.targetMargin),
    }));
  };

  const importPrintFile = useCallback(async (file) => {
    if (!file || !profiles.length) return;
    const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    importToken.current = token;
    onUploadConsumed?.();
    setError('');

    const profile = profiles.find((item) => item.isDefault) || profiles[0];
    const baseSource = sourceMetadata(file);
    setDraft({
      ...defaultDraft,
      estimateId: '',
      name: fileBaseName(file.name),
      profileId: profile?.id || '',
      profileSnapshot: null,
      targetMargin: numberValue(profile?.targetMargin),
      source: baseSource,
    });

    if (isModelPrintFile(file)) {
      markAppBusy(token);
      setImportState({ status: 'working', progress: 0.12, label: fileKind(file) === '3mf' ? 'Reading 3MF slicer data and geometry' : 'Analyzing STL geometry' });
      try {
        const analysis = await analyzeModelFile(file, { materialType: profile?.materialType || '' });
        if (importToken.current !== token) return;
        const importedFields = Object.keys(analysis.fields);
        setDraft((current) => ({
          ...current,
          ...analysis.fields,
          source: {
            ...baseSource,
            detectedSlicer: analysis.detectedSlicer,
            detectedMaterial: analysis.detectedMaterial,
            analysis: {
              method: analysis.method,
              confidence: analysis.extractionConfidence,
              exactFields: analysis.exactFields,
              importedFields,
              fieldConfidence: analysis.fieldConfidence,
              geometry: analysis.geometry,
              notes: analysis.notes,
              rawText: analysis.rawText,
            },
          },
        }));
        setImportState({
          status: 'complete',
          progress: 1,
          label: analysis.method === 'slicer-metadata'
            ? 'Sliced 3MF filament and print-time totals imported for review.'
            : 'Geometry-based filament and print-time estimates imported for review.',
        });
        setSaveStatus(`New estimate started with ${profile?.name || 'your default profile'}.`);
      } catch (analysisError) {
        if (importToken.current !== token) return;
        setImportState({ status: 'error', progress: 0, label: 'MM could not calculate reliable values from this model file.' });
        setError(analysisError?.message || 'The model file could not be analyzed. Enter the slicer totals manually.');
      } finally {
        clearAppBusy(token);
      }
      return;
    }

    if (!isImageFile(file)) {
      setImportState({ status: 'complete', progress: 1, label: 'File attached. Enter slicer values to finish the estimate.' });
      setSaveStatus(`New estimate started with ${profile?.name || 'your default profile'}.`);
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      setImportState({ status: 'error', progress: 0, label: 'This image is larger than the 25 MB OCR limit.' });
      return;
    }

    markAppBusy(token);
    setImportState({ status: 'working', progress: 0.02, label: 'Starting private on-device OCR' });
    try {
      const ocr = await recognizeSlicerScreenshot(file, (progress) => {
        if (importToken.current !== token) return;
        setImportState({ status: 'working', ...progress });
      });
      if (importToken.current !== token) return;

      const importedFields = Object.keys(ocr.fields);
      setDraft((current) => ({
        ...current,
        ...ocr.fields,
        source: {
          ...baseSource,
          detectedSlicer: ocr.detectedSlicer,
          detectedMaterial: ocr.detectedMaterial,
          ocr: {
            confidence: ocr.extractionConfidence,
            engineConfidence: ocr.ocrConfidence,
            passes: ocr.passes,
            importedFields,
            fieldConfidence: ocr.fieldConfidence,
            rawText: ocr.rawText,
          },
        },
      }));
      setImportState({
        status: 'complete',
        progress: 1,
        label: importedFields.length
          ? `${importedFields.length} estimate value${importedFields.length === 1 ? '' : 's'} imported for review.`
          : 'OCR finished, but no safe pricing values were identified. Enter them manually.',
      });
      setSaveStatus(`New estimate started with ${profile?.name || 'your default profile'}.`);
    } catch (ocrError) {
      if (importToken.current !== token) return;
      setImportState({ status: 'error', progress: 0, label: 'OCR could not read this image. The estimate is still ready for manual entry.' });
      setError(ocrError?.message || 'The screenshot could not be analyzed.');
    } finally {
      clearAppBusy(token);
    }
  }, [onUploadConsumed, profiles]);

  useEffect(() => {
    if (!initialUpload?.file || !profiles.length || loadingEstimate) return;
    importPrintFile(initialUpload.file);
  }, [importPrintFile, initialUpload, loadingEstimate, profiles.length]);

  const importCatalogProduct = useCallback((product) => {
    if (!product || !profiles.length) return;
    importToken.current = '';
    onProductConsumed?.();
    setError('');
    const profile = profiles.find((item) => item.id === product.profileId)
      || profiles.find((item) => item.isDefault)
      || profiles[0];
    setDraft(createFreshEstimateDraft({
      name: product.name || 'Catalog product',
      profileId: profile?.id || '',
      targetMargin: numberValue(product.targetMargin, numberValue(profile?.targetMargin)),
      filamentGrams: numberValue(product.filamentGrams),
      printHours: numberValue(product.printHours),
      printMinutes: numberValue(product.printMinutes),
      averagePowerWatts: numberValue(product.averagePowerWatts),
      laborMinutes: numberValue(product.laborMinutes),
      quantity: Math.max(1, numberValue(product.quantity, 1)),
      additionalCost: numberValue(product.additionalCost),
      source: {
        name: product.name || 'Catalog product',
        kind: 'catalog',
        catalogProductId: product.id || '',
        importedAt: new Date().toISOString(),
        ocr: null,
      },
    }));
    setImportState({ status: 'complete', progress: 1, label: 'Catalog production assumptions loaded for review.' });
    setSaveStatus(`New estimate started from ${product.name || 'your catalog product'}.`);
  }, [onProductConsumed, profiles]);

  useEffect(() => {
    if (!initialProduct?.product || !profiles.length || loadingEstimate) return;
    importCatalogProduct(initialProduct.product);
  }, [importCatalogProduct, initialProduct, loadingEstimate, profiles.length]);

  const resetDraft = () => {
    importToken.current = '';
    setImportState({ status: 'idle', progress: 0, label: '' });
    const profile = profiles.find((item) => item.isDefault) || profiles[0];
    setDraft({
      ...defaultDraft,
      profileId: profile?.id || '',
      targetMargin: numberValue(profile?.targetMargin),
      source: null,
    });
    setSaveStatus('New estimate ready.');
    onNewEstimate?.();
  };

  const saveEstimate = async () => {
    if (!user || !db || !selectedProfile) return;
    const name = String(draft.name || '').trim();
    if (!name) {
      setError('Give this estimate a name before saving it.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const reference = draft.estimateId
        ? doc(db, 'users', user.uid, 'estimates', draft.estimateId)
        : doc(collection(db, 'users', user.uid, 'estimates'));
      const payload = {
        ownerId: user.uid,
        name,
        profileId: draft.profileId || '',
        profileName: selectedProfile.name || 'Saved profile',
        profileSnapshot: copyProfileSnapshot(selectedProfile),
        inputs: copyEstimateInputs(draft),
        results: copyResults(results),
        source: draft.source || null,
        updatedAt: serverTimestamp(),
      };
      if (!draft.estimateId) payload.createdAt = serverTimestamp();

      await setDoc(reference, payload, { merge: true });
      setDraft((current) => ({ ...current, estimateId: reference.id, name }));
      setSaveStatus(draft.estimateId ? 'Estimate updated in Firebase.' : 'Estimate saved to Firebase.');
    } catch (saveError) {
      setError(readableError(saveError));
    } finally {
      setSaving(false);
    }
  };

  const copySummary = async () => {
    if (!selectedProfile) return;
    const summary = [
      `MakerMargin estimate: ${draft.name || 'Untitled estimate'}`,
      `Cost profile: ${selectedProfile.name}`,
      `Quantity: ${results.quantity}`,
      `Total production cost: ${currency.format(results.totalBatchCost)}`,
      `Cost per piece: ${currency.format(results.costPerPiece)}`,
      `Recommended price per piece: ${currency.format(results.recommendedPricePerPiece)}`,
      `Recommended batch price: ${currency.format(results.recommendedBatchPrice)}`,
      `Estimated profit per piece: ${currency.format(results.profitPerPiece)}`,
      `Target margin: ${results.targetMargin.toFixed(1)}%`,
      draft.source?.name ? `Source file: ${draft.source.name}` : '',
    ].filter(Boolean).join('\n');

    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 2400);
    } catch {
      setError('The browser blocked clipboard access. You can still read every result on screen.');
    }
  };

  if (!user) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><Calculator size={30} /></span>
        <p className="eyebrow">Live pricing workspace</p>
        <h1>Sign in to calculate from your saved costs.</h1>
        <p>MakerMargin connects each estimate to your private filament, electricity, machine, labor, waste, and margin assumptions.</p>
        <div className="signed-out-state__actions">
          <button className="button button--primary button--large" type="button" onClick={() => onRequestAuth('signup')}>Create account</button>
          <button className="button button--ghost button--large" type="button" onClick={() => onRequestAuth('signin')}>Sign in</button>
        </div>
      </section>
    );
  }

  if (loadingProfiles || loadingEstimate) {
    return (
      <section className="calculator-loading brushed-panel" aria-live="polite">
        <span><Calculator size={28} /></span>
        <h1>{loadingEstimate ? 'Opening your saved estimate…' : 'Preparing your pricing workspace…'}</h1>
        <p>{loadingEstimate ? 'Restoring the job inputs and saved pricing snapshot from Firebase.' : 'Loading your saved cost profiles from Firebase.'}</p>
      </section>
    );
  }

  if (!profiles.length) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><Leaf size={30} /></span>
        <p className="eyebrow">One step before pricing</p>
        <h1>Create a cost profile first.</h1>
        <p>The calculator needs your filament price, electricity rate, machine cost, labor rate, waste allowance, and target margin before it can produce an honest quote.</p>
        <div className="signed-out-state__actions">
          <button className="button button--primary button--large" type="button" onClick={() => onNavigate('cost-profiles')}>Open cost profiles</button>
        </div>
      </section>
    );
  }

  return (
    <section className="calculator-page">
      <header className="calculator-page__hero">
        <div>
          <p className="eyebrow">Phase 5 · Upload-assisted pricing</p>
          <h1>Turn print data into a defensible price.</h1>
          <p>Drop in a slicer screenshot or print file, review any imported values, and finish the quote from your real saved costs.</p>
        </div>
        <div className="calculator-page__hero-actions">
          <span><Cloud size={15} /> {draft.estimateId ? 'Linked to a saved estimate' : 'Draft saved on this device'}</span>
          <div className="calculator-page__button-row">
            <button className="button button--primary" type="button" onClick={saveEstimate} disabled={saving}><Save size={17} /> {saving ? 'Saving…' : draft.estimateId ? 'Update estimate' : 'Save estimate'}</button>
            <button className="button button--ghost" type="button" onClick={resetDraft}><RefreshCcw size={17} /> New estimate</button>
          </div>
        </div>
      </header>

      {error && <div className="config-banner config-banner--error" role="alert"><ShieldCheck size={18} /><span>{error}</span><button type="button" onClick={() => setError('')}>Dismiss</button></div>}
      {saveStatus && <div className="calculator-save-status" role="status"><Check size={16} /> {saveStatus}</div>}

      <div className="calculator-layout">
        <section className="calculator-workspace brushed-panel">
          <div className="section-heading calculator-workspace__heading">
            <div><p className="eyebrow">Estimate inputs</p><h2>Print and job details</h2></div>
            <span className="calculator-live"><i /> Live calculation</span>
          </div>

          <div className="calculator-profile-strip">
            <span className="calculator-profile-strip__icon"><Leaf size={20} /></span>
            <label>
              <small>Cost profile</small>
              <select value={draft.profileId} onChange={selectProfile}>
                {draft.profileSnapshot && draft.profileId && !profiles.some((profile) => profile.id === draft.profileId) && (
                  <option value={draft.profileId}>{draft.profileSnapshot.name || 'Saved profile'} — Saved snapshot</option>
                )}
                {profiles.map((profile) => (
                  <option value={profile.id} key={profile.id}>{profile.name}{profile.isDefault ? ' — Default' : ''}</option>
                ))}
              </select>
            </label>
            <div><small>Material cost</small><strong>{preciseCurrency.format(costPerGram(selectedProfile))}<span>/g</span></strong></div>
            <div><small>Machine cost</small><strong>{currency.format(numberValue(selectedProfile?.machineRate))}<span>/hr</span></strong></div>
            <button type="button" onClick={() => onNavigate('cost-profiles')}>Edit profile</button>
          </div>

          <section className={`calculator-source-panel ${importState.status === 'working' ? 'calculator-source-panel--working' : ''}`}>
            {!draft.source ? (
              <div className="calculator-source-panel__empty">
                <div>
                  <span><FileUp size={20} /></span>
                  <div><strong>Start from a slicer file or screenshot</strong><small>An upload creates a new estimate with your default cost profile.</small></div>
                </div>
                <UploadStarter compact onFile={importPrintFile} />
              </div>
            ) : (
              <>
                <div className="calculator-source-panel__head">
                  <span className="calculator-source-panel__icon">{draft.source.kind === 'image' ? <FileImage size={21} /> : <FileCheck2 size={21} />}</span>
                  <div className="calculator-source-panel__identity">
                    <small>Estimate source</small>
                    <strong>{draft.source.name}</strong>
                    <span>{draft.source.kind === 'catalog' ? 'Catalog product' : `${readableFileSize(draft.source.size)} · ${draft.source.kind.toUpperCase()}`}</span>
                  </div>
                  <div className="calculator-source-panel__actions">
                    <label className="button button--ghost button--small">
                      <FileUp size={15} /> Replace
                      <input className="visually-hidden" type="file" accept=".png,.jpg,.jpeg,.webp,.stl,.3mf,.gcode,.gco" onChange={(event) => { const file = event.target.files?.[0]; if (file) importPrintFile(file); event.target.value = ''; }} />
                    </label>
                    <button className="icon-button" type="button" onClick={() => { importToken.current = ''; setDraft((current) => ({ ...current, source: null })); setImportState({ status: 'idle', progress: 0, label: '' }); }} aria-label="Remove source file"><X size={17} /></button>
                  </div>
                </div>

                {importState.status === 'working' && (
                  <div className="ocr-progress" role="status" aria-live="polite">
                    <div><LoaderCircle className="spin" size={17} /><span>{importState.label}</span><strong>{Math.round((importState.progress || 0) * 100)}%</strong></div>
                    <span><i style={{ width: `${Math.round((importState.progress || 0) * 100)}%` }} /></span>
                    <small>OCR runs in your browser. The screenshot is not uploaded to MakerMargin.</small>
                  </div>
                )}

                {importState.status !== 'working' && draft.source.kind === 'image' && (
                  <div className="ocr-result">
                    <span className={`ocr-result__status ${draft.source.ocr?.confidence >= 60 ? 'ocr-result__status--good' : 'ocr-result__status--review'}`}>
                      {draft.source.ocr?.importedFields?.length ? <ScanText size={16} /> : <AlertTriangle size={16} />}
                      {draft.source.ocr ? confidenceLabel(draft.source.ocr.confidence) : 'Image attached'}
                    </span>
                    <div className="ocr-result__copy">
                      <strong>{importState.label || 'Review the detected values before pricing.'}</strong>
                      <small>
                        {draft.source.detectedSlicer || 'Unknown slicer'}
                        {draft.source.detectedMaterial ? ` · ${draft.source.detectedMaterial} detected` : ''}
                        {draft.source.ocr?.passes ? ` · ${draft.source.ocr.passes} OCR pass${draft.source.ocr.passes === 1 ? '' : 'es'}` : ''}
                      </small>
                    </div>
                    {Boolean(draft.source.ocr?.importedFields?.length) && (
                      <div className="ocr-result__fields">
                        {draft.source.ocr.importedFields.map((field) => <span key={field}><Check size={13} /> {importedFieldLabel(field)}</span>)}
                      </div>
                    )}
                  </div>
                )}

                {draft.source.kind === 'catalog' && (
                  <div className="ocr-result">
                    <span className="ocr-result__status ocr-result__status--good"><Check size={16} /> Catalog defaults</span>
                    <div className="ocr-result__copy"><strong>{importState.label || 'Catalog production assumptions restored.'}</strong><small>Review the cost profile, quantity, production inputs, and margin before saving.</small></div>
                  </div>
                )}

                {draft.source.analysis && (
                  <div className="ocr-result">
                    <span className={`ocr-result__status ${draft.source.analysis.method === 'slicer-metadata' ? 'ocr-result__status--good' : 'ocr-result__status--review'}`}>
                      {draft.source.analysis.method === 'slicer-metadata' ? <ScanText size={16} /> : <AlertTriangle size={16} />}
                      {draft.source.analysis.method === 'slicer-metadata' ? 'Slicer totals' : 'Geometry estimate'}
                    </span>
                    <div className="ocr-result__copy">
                      <strong>{importState.label || 'Review the imported model values before pricing.'}</strong>
                      <small>{draft.source.analysis.notes}</small>
                    </div>
                    {Boolean(draft.source.analysis.importedFields?.length) && (
                      <div className="ocr-result__fields">
                        {draft.source.analysis.importedFields.map((field) => <span key={field}><Check size={13} /> {importedFieldLabel(field)}</span>)}
                      </div>
                    )}
                  </div>
                )}

                {draft.source.kind !== 'image' && draft.source.kind !== 'catalog' && !draft.source.analysis && (
                  <div className="ocr-result">
                    <span className="ocr-result__status"><FileCheck2 size={16} /> File attached</span>
                    <div className="ocr-result__copy"><strong>{importState.label}</strong><small>This file did not provide reliable slicer totals. Enter the material and machine-time values manually.</small></div>
                  </div>
                )}
              </>
            )}
          </section>

          <div className="calculator-section">
            <div className="calculator-section__title"><Package size={17} /><div><strong>Job identity and batch</strong><small>Filament and print time should describe the entire build plate or batch.</small></div></div>
            <div className="calculator-input-grid calculator-input-grid--job">
              <Field label="Estimate name" help="Used in saved pricing and quote summaries" guidanceKey="estimateName" className="calculator-field--wide">
                <input value={draft.name} onChange={update('name')} placeholder="Name this estimate" />
              </Field>
              <Field label="Quantity" help="Changing this scales filament and print time" guidanceKey="quantity" suffix="pcs">
                <input type="number" inputMode="numeric" min="1" step="1" value={draft.quantity} onFocus={beginQuantityEdit} onChange={updateQuantity} onBlur={finishQuantityEdit} />
              </Field>
              <Field label="Filament used" help="Total slicer estimate" guidanceKey="filamentGrams" suffix="g">
                <input type="number" inputMode="decimal" min="0" step="0.1" value={draft.filamentGrams} onChange={update('filamentGrams')} />
              </Field>
              <Field label="Print hours" help="Total machine time" guidanceKey="printHours" suffix="hr">
                <input type="number" inputMode="numeric" min="0" step="1" value={draft.printHours} onChange={update('printHours')} />
              </Field>
              <Field label="Print minutes" help="Remaining minutes" guidanceKey="printMinutes" suffix="min">
                <input type="number" inputMode="numeric" min="0" max="59" step="1" value={draft.printMinutes} onChange={update('printMinutes')} />
              </Field>
            </div>
          </div>

          <div className="calculator-section">
            <div className="calculator-section__title"><Factory size={17} /><div><strong>Operating assumptions</strong><small>These can vary by job without changing the saved cost profile.</small></div></div>
            <div className="calculator-input-grid">
              <Field label="Average power draw" help="Printer average during this job" guidanceKey="averagePowerWatts" suffix="W">
                <input type="number" inputMode="decimal" min="0" step="1" value={draft.averagePowerWatts} onChange={update('averagePowerWatts')} />
              </Field>
              <Field label="Hands-on labor" help="Setup, cleanup, finishing" guidanceKey="laborMinutes" suffix="min">
                <input type="number" inputMode="decimal" min="0" step="1" value={draft.laborMinutes} onChange={update('laborMinutes')} />
              </Field>
              <Field label="Additional batch cost" help="Packaging or job-specific fees" guidanceKey="additionalCost" prefix="$">
                <input type="number" inputMode="decimal" min="0" step="0.01" value={draft.additionalCost} onChange={update('additionalCost')} />
              </Field>
              <Field label="Target profit margin" help={`Profile default: ${numberValue(selectedProfile?.targetMargin).toFixed(1)}%`} guidanceKey="targetMargin" suffix="%">
                <input type="number" inputMode="decimal" min="0" max="94.99" step="0.1" value={draft.targetMargin} onChange={update('targetMargin')} />
              </Field>
            </div>
          </div>

          <aside className="calculator-formula-note">
            <span><Gauge size={20} /></span>
            <div><strong>Margin, not markup</strong><p>MakerMargin divides the cost by one minus your target margin. That protects the actual percentage of the selling price you intend to keep.</p></div>
          </aside>
        </section>

        <aside className="calculator-results metal-panel">
          <div className="calculator-results__head">
            <div><p className="eyebrow">Live result</p><h2>Recommended pricing</h2></div>
            <span><Sparkles size={16} /> Calculated</span>
          </div>

          <div className="calculator-price-hero">
            <small>Recommended price per piece</small>
            <strong>{currency.format(results.recommendedPricePerPiece)}</strong>
            <span>{results.targetMargin.toFixed(1)}% target margin · {results.quantity} piece{results.quantity === 1 ? '' : 's'}</span>
          </div>

          <div className="calculator-result-grid">
            <article><span><Coins size={16} /> Break-even per piece</span><strong>{currency.format(results.costPerPiece)}</strong></article>
            <article><span><BadgeDollarSign size={16} /> Profit per piece</span><strong>{currency.format(results.profitPerPiece)}</strong></article>
            <article><span><WalletCards size={16} /> Batch selling price</span><strong>{currency.format(results.recommendedBatchPrice)}</strong></article>
            <article><span><Percent size={16} /> Total batch profit</span><strong>{currency.format(results.totalProfit)}</strong></article>
          </div>

          <section className="calculator-breakdown">
            <div className="calculator-breakdown__head"><strong>Cost breakdown</strong><span>{currency.format(results.totalBatchCost)} total</span></div>
            <BreakdownRow icon={Leaf} label="Material" detail={`${results.filamentGrams.toFixed(1)} g total`} value={results.materialCost} total={results.totalBatchCost} />
            <BreakdownRow icon={Zap} label="Electricity" detail={`${results.electricityKwh.toFixed(3)} kWh`} value={results.electricityCost} total={results.totalBatchCost} />
            <BreakdownRow icon={Clock3} label="Machine" detail={`${results.totalPrintHours.toFixed(2)} hours`} value={results.machineCost} total={results.totalBatchCost} />
            <BreakdownRow icon={UserRound} label="Labor" detail={`${results.laborMinutes.toFixed(0)} minutes`} value={results.laborCost} total={results.totalBatchCost} />
            <BreakdownRow icon={TimerReset} label="Failure allowance" detail={`${numberValue(selectedProfile?.wastePercent).toFixed(1)}% reserve`} value={results.failureAllowance} total={results.totalBatchCost} />
            <BreakdownRow icon={Package} label="Fixed and extra" detail="Profile + job costs" value={results.fixedCost} total={results.totalBatchCost} />
          </section>

          <div className="calculator-results__actions">
            <button className="button button--primary button--large button--full" type="button" onClick={saveEstimate} disabled={saving}>
              <Save size={18} /> {saving ? 'Saving estimate…' : draft.estimateId ? 'Update saved estimate' : 'Save estimate'}
            </button>
            <button className="button button--ghost button--large button--full" type="button" onClick={copySummary}>
              {copied ? <><Check size={18} /> Quote summary copied</> : <><Clipboard size={18} /> Copy quote summary</>}
            </button>
          </div>
          <p className="calculator-results__save-note">Saved estimates can now be reopened, edited, duplicated, and recalculated from the Estimates library.</p>
        </aside>
      </div>
    </section>
  );
}
